import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "./api";
import { getToken, setToken, clearToken, subscribeTokenChanges } from "./token";
import { configureIAP, disconnectIAP } from "./iap";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionVersion = useRef(0);

  // Verify the stored session on mount and after another tab changes it.
  useEffect(() => {
    let active = true;
    const restoreSession = () => {
      const token = getToken();
      const version = ++sessionVersion.current;
      const isCurrent = () => active && sessionVersion.current === version;
      const ownsSession = () => isCurrent() && getToken() === token;
      if (!token) {
        setLoading(false);
        return;
      }
      setLoading(true);
      api.get("/auth/me", { timeout: 30000 })
        .then((r) => { if (ownsSession()) setUser(r.data); })
        .catch(() => {
          if (!ownsSession()) return;
          console.warn("Auth restore failed; clearing current session");
          clearToken();
        })
        .finally(() => { if (isCurrent()) setLoading(false); });
    };
    const unsubscribe = subscribeTokenChanges(() => {
      // Do not display the old account while requests now use another token.
      setUser(null);
      disconnectIAP().catch(() => console.warn("IAP disconnect failed"));
      restoreSession();
    });
    restoreSession();
    return () => {
      active = false;
      sessionVersion.current += 1;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (user?.id) configureIAP(user.id).catch((err) => console.warn("IAP configure failed", err));
  }, [user?.id]);

  const authenticate = async (path, data) => {
    const version = ++sessionVersion.current;
    const r = await api.post(path, data, { timeout: 30000 });
    // Logout, unmount, or a newer sign-in owns any later session transition.
    if (version !== sessionVersion.current) {
      throw new Error("This sign-in request was superseded. Please try again.");
    }
    setToken(r.data.token);
    setUser(r.data.user);
    setLoading(false);
    return r.data;
  };

  const login = async (email, password) => {
    const data = await authenticate("/auth/login", { email, password });
    return data.user;
  };

  const register = async (data) => {
    return authenticate("/auth/register", data);
  };

  const logout = () => {
    sessionVersion.current += 1;
    disconnectIAP().catch((err) => console.warn("IAP disconnect failed", err));
    clearToken();
    setUser(null);
    setLoading(false);
  };

  const refresh = async () => {
    const version = sessionVersion.current;
    const token = getToken();
    if (!token) return;
    const r = await api.get("/auth/me", { timeout: 30000 });
    if (version === sessionVersion.current && getToken() === token) setUser(r.data);
  };

  return <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
